import type { UseQueryResult } from "@tanstack/react-query";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import {
  classifySectionFailure,
  queryKeys,
  type SectionConfig,
  type SectionFailure,
} from "@/api/hooks";
import { groupSummaryFields, summaryProvenanceCounts } from "@/lib/summaryGroups";
import { ErrorState, UnavailableState } from "@/ui/states";

/**
 * The clinician-readable half of the Clinical Summary: what this project's
 * summary is configured to say (#373).
 *
 * ## Every failure is stated, and none is replaced
 *
 * The epic's binding line is that a `501` is never silently replaced. This
 * component holds that for all four refusals the sections route can answer,
 * because "replaced by an empty Summary" is the same defect whichever status
 * caused it — and the failures below are genuinely different problems with
 * genuinely different owners:
 *
 *  - **501** — the Editor Service has not built the endpoint in this
 *    deployment. Terminal; `queryClient.ts` never retries it and no control
 *    here offers to, because a retry that cannot succeed is exactly the dead
 *    control ADR 0001 forbids.
 *  - **409 `projection_identity_mismatch`** — a stored identity is behind the
 *    runtime: this project's pinned seed against the mounted base, or the
 *    draft's pinned base against the current one. Terminal too
 *    (`TERMINAL_CONFLICT_CODES`), because the recovery is a state change and
 *    not a second request.
 *  - **409, other** — stored state that cannot be composed at all: an
 *    unplaceable pin, a stored field with no target in the pack. The envelope
 *    names the first offender with a dotted pointer, so #488's amendment keeps
 *    it off this route: the copy states that the service traced the failure to
 *    one stored value, and `classifySectionFailure` reduces the pointer to that
 *    boolean before any surface can render it.
 *  - **503** — the composition environment is unavailable. The one retryable
 *    member, and the state every editor lane that mounts no engine sits in.
 *
 * Nothing here falls through to a partial render: each arm returns instead of
 * the content, so there is no path on which some of the summary appears beside
 * an explanation of why it did not load. The titles are keyed with `satisfies`
 * on `SectionFailure["kind"]`, the same way `CandidateFreezeControl` keys its
 * copy, so a new failure kind fails the build rather than rendering blank.
 *
 * ## Why not `QueryErrorState` for all of it
 *
 * That component owns the distinctions every surface shares — expired session,
 * denial, not-implemented — and the last two arms delegate to it. What it
 * cannot do is tell a stale projection from an unreachable engine: both are
 * `ErrorState` with the caller's one `title`. #373 asks for those to be
 * distinct and to name what is wrong, which is what the arms below add. The
 * generic `SectionDiagnostics` keeps routing everything through
 * `QueryErrorState`, since it renders one body for six slugs and has no copy
 * that could name a projection identity.
 */
export function SummaryContent({
  projectId,
  sectionQuery,
}: {
  projectId: string;
  sectionQuery: UseQueryResult<SectionConfig>;
}) {
  if (sectionQuery.isPending) {
    return (
      <QueryPendingState
        isPaused={sectionQuery.isPaused}
        failureReason={sectionQuery.failureReason}
        label="Loading the clinical summary"
        errorTitle={FAILURE_TITLE.error}
        rows={3}
        queryKey={queryKeys.section(projectId, "summary")}
      />
    );
  }

  if (sectionQuery.isError) {
    return (
      <SummaryContentFailure
        failure={classifySectionFailure(sectionQuery.error)}
        error={sectionQuery.error}
        onRetry={() => void sectionQuery.refetch()}
      />
    );
  }

  const { fields } = sectionQuery.data;

  if (fields.length === 0) {
    // A real, successful, empty projection — distinct from every failure above
    // and said as such. The pack this project is based on serves no summary
    // content; that is a fact about the pack, not a fault.
    return (
      <p className="text-sm text-muted-foreground">
        The Editor Service composed this project&apos;s summary and it carries
        no values. Nothing failed — the base pack this project is built on
        serves no summary content.
      </p>
    );
  }

  const groups = groupSummaryFields(fields);
  const counts = summaryProvenanceCounts(fields);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        The effective summary for this project: pinned base content plus this
        project&apos;s overlay, composed by the Editor Service. {counts.total}{" "}
        {counts.total === 1 ? "value" : "values"} — {counts.fromBase} from the
        base pack, {counts.fromProject} supplied by this project,{" "}
        {counts.locked} locked. Every value is read-only here; authoring
        arrives with its own ticket, and a control that cannot author is not
        rendered as though it can.
      </p>

      {groups.map((group) => (
        <section key={group.key} className="space-y-3">
          <div className="space-y-0.5">
            <h3 className="text-sm font-semibold text-foreground">
              {group.label}
            </h3>
            {group.description ? (
              <p className="text-sm text-muted-foreground">
                {group.description}
              </p>
            ) : null}
          </div>
          {/*
            #488: how much this group holds and where it came from, not the
            values themselves.

            `SectionFieldList` stood here and rendered every field of the group
            — on the ratified base pack that is 171 rows of summary prompt text
            and JSON, keyed by dotted address, on a Project route. The amendment
            forbids all three.

            This section has NO bounded replacement to swap to. `summary` is
            deliberately outside #487 — `ClinicalSummarySectionSlug` excludes it
            and the service answers 422 for its summary — so there is no
            semantic item list for it, and inventing one in the browser is
            exactly the "browser-side reconstruction of semantics from raw
            paths" this ticket lists as a non-goal.

            What is left is what can be said honestly without the payload: the
            group's own heading, which `groupSummaryFields` derives from the
            SERVICE'S addresses, and a count. The values remain in the
            authoritative projection for validation and support, which is the
            other thing the ticket requires.
          */}
          <p className="text-sm text-muted-foreground">
            {group.fields.length === 1
              ? "1 value, composed by the Editor Service."
              : `${group.fields.length} values, composed by the Editor Service.`}{" "}
            Their content is part of the authoritative projection and is not
            shown here.
          </p>
        </section>
      ))}
    </div>
  );
}

/**
 * One heading per failure kind.
 *
 * `satisfies Record<SectionFailure["kind"], string>` is the load-bearing part:
 * a kind added to the union without a heading here stops the build, which is
 * the mechanism that keeps a new refusal from silently rendering as a blank
 * summary. `denied` and `error` share a heading because they share an arm —
 * the shared vocabulary decides what each of them says.
 */
const FAILURE_TITLE = {
  unavailable: "The Clinical Summary is not available in this deployment",
  stale: "This project's summary is out of date with the deployed clinical content",
  uncomposable: "This project's summary could not be composed",
  upstream: "The Editor Service could not reach the clinical engine",
  denied: "This project's summary could not be loaded",
  error: "This project's summary could not be loaded",
} satisfies Record<SectionFailure["kind"], string>;

function SummaryContentFailure({
  failure,
  error,
  onRetry,
}: {
  failure: SectionFailure;
  error: unknown;
  onRetry: () => void;
}) {
  switch (failure.kind) {
    case "unavailable":
      return (
        <UnavailableState
          title={FAILURE_TITLE.unavailable}
          description="The Editor Service accepts this request but has not implemented the summary projection here, so there is nothing to show. It resolves when the endpoint is deployed — there is nothing to retry, and no part of the summary is being shown from anywhere else."
          blockedBy="HTTP 501 not_implemented — Editor Service endpoint not built in this deployment."
        />
      );
    case "stale":
      return (
        <ErrorState
          title={FAILURE_TITLE.stale}
          description="A stored identity disagrees with what is deployed — this project's pinned base, or the draft's. Until they agree the Editor Service will not compose a summary, and nothing partial is shown in its place. The fix is a state change made by whoever owns the base: re-import the seed catalog, refresh the base, or refresh the draft. Asking again changes nothing."
        />
      );
    case "uncomposable":
      return (
        <ErrorState
          title={FAILURE_TITLE.uncomposable}
          description={
            // #488: WHETHER the service pinned the failure to one stored
            // value, never its address. This interpolation was the same
            // `failure.field` the section body carried, on the same kind of
            // route — the Clinical Summary is a Project route — and the first
            // round of #488 changed only one of the two. The amendment's
            // prohibition is not surface-specific, so neither is the fix.
            failure.namedField
              ? "Stored configuration cannot be composed into an effective summary. The Editor Service traced it to one specific stored value — an adopted asset with no home in this pack, or a stored value with no target — and recorded which; that address is machine-level detail and is not shown on a clinical route. Nothing partial is shown, and asking again changes nothing until that is resolved."
              : "Stored configuration cannot be composed into an effective summary — an adopted asset with no home in this pack, or a stored value with no target. The Editor Service did not trace it to a single value. Nothing partial is shown, and asking again changes nothing until that is resolved."
          }
        />
      );
    case "upstream":
      return (
        <ErrorState
          title={FAILURE_TITLE.upstream}
          description="Composing a summary needs the pinned engine and the pack mounts, and one of them is unavailable right now. Nothing about this project's stored configuration is wrong, and nothing is shown in place of the summary. This one is worth asking again."
          onRetry={onRetry}
        />
      );
    // 401, 403, 404 and everything unclassified keep the shared vocabulary,
    // which already renders each of them correctly and decides the retry from
    // `isTerminalQueryError`.
    case "denied":
    case "error":
      return (
        <QueryErrorState
          error={error}
          subject="Clinical summary values"
          title={FAILURE_TITLE.error}
          description="Nothing is shown in place of it — an empty summary here would read as a project that produces no summary."
          onRetry={onRetry}
        />
      );
  }
}
